import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from "react";

export type ThemeMode = "light" | "dark";

type ThemeModeContextValue = {
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  toggleThemeMode: () => void;
};

const STORAGE_KEY = "evzone-theme-mode";

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

const readInitialThemeMode = (): ThemeMode => {
  if (typeof window === "undefined") return "light";
  return window.localStorage.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
};

export function ThemeModeProvider({ children }: PropsWithChildren) {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(readInitialThemeMode);

  useEffect(() => {
    document.documentElement.dataset.theme = themeMode;
    document.documentElement.style.colorScheme = themeMode;
    window.localStorage.setItem(STORAGE_KEY, themeMode);
  }, [themeMode]);

  const value = useMemo<ThemeModeContextValue>(() => ({
    themeMode,
    setThemeMode: setThemeModeState,
    toggleThemeMode: () => setThemeModeState((current) => current === "light" ? "dark" : "light"),
  }), [themeMode]);

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
}

export function useThemeMode() {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within ThemeModeProvider.");
  }
  return context;
}

